/**
 * 题目类型元结构系统 - 核心类型定义
 */

import type { QuestionType } from '../types';

// 从主类型文件导入共享类型（避免重复定义）
export type {
  ValidationError,
  FieldDefinition,
  RenderConfig,
  ExamSpecificQuestionTypeDefinition,
  SectionQuestionTypeConstraint,
} from '../types';

/**
 * 验证结果
 */
export interface ValidationResult {
  /** true = 无 blocking 错误，可导入（仍可能有 warning） */
  valid: boolean;
  errors: ValidationError[];
  /** Convenience alias: errors with severity === 'warning' */
  warnings?: ValidationError[];
}

/**
 * 题目类型元数据
 */
export interface QuestionTypeMetadata {
  /** 题目类型 */
  type: QuestionType;
  /** 类型名称 */
  name: string;
  /** 类型描述 */
  description: string;

  /** 必需字段列表 */
  requiredFields: string[];
  /** 可选字段列表 */
  optionalFields: string[];
  /** 字段定义 */
  fields: Record<string, FieldDefinition>;

  /** 验证函数 */
  validate: (question: any) => ValidationResult;

  /** 渲染配置（前端使用） */
  renderConfig?: RenderConfig;
}

/**
 * Schema 生成器函数类型
 * 注意：这里不直接依赖 zod，而是返回一个可以转换为 zod schema 的描述对象
 */
export interface SchemaDescriptor {
  type: 'object';
  properties: Record<
    string,
    {
      type: string;
      required?: boolean;
      validator?: (value: any) => boolean | string;
      defaultValue?: any;
    }
  >;
}

/**
 * 带 Schema 生成器的元数据（可选）
 */
export interface QuestionTypeMetadataWithSchema extends QuestionTypeMetadata {
  /** 生成 Schema 描述符 */
  generateSchemaDescriptor?: () => SchemaDescriptor;
}

/**
 * 基础字段定义 - 用于构建字段定义
 */
export function createFieldDefinition(
  name: string,
  type: FieldDefinition['type'],
  required: boolean,
  description?: string,
  options?: {
    validator?: (value: any) => boolean | string;
    defaultValue?: any;
  }
): FieldDefinition {
  return {
    name,
    type,
    required,
    description,
    ...options,
  };
}

/**
 * 创建考试特定的题目类型定义
 */
export function createExamQuestionTypeDefinition(
  id: string,
  baseType: QuestionType,
  name: string,
  options: {
    requiredStimulus?: ExamSpecificQuestionTypeDefinition['requiredStimulus'];
    responseMode?: ExamSpecificQuestionTypeDefinition['responseMode'];
    fieldOverrides?: Partial<Record<string, FieldDefinition>>;
    extraFields?: Record<string, FieldDefinition>;
    renderConfigOverride?: Partial<RenderConfig>;
    customValidator?: (question: any) => ValidationError[];
    constraints?: Record<string, any>;
  } = {}
): ExamSpecificQuestionTypeDefinition {
  return {
    id,
    baseType,
    name,
    ...options,
  };
}

/**
 * 创建 Section 题目类型约束
 * countLimit 仅供套卷/模拟卷模板可选使用；AI 生题题量由调用方 count 传参。
 */
export function createSectionConstraint(
  questionTypeId: string,
  options: {
    countLimit?: { min: number; max: number };
    fieldConstraints?: SectionQuestionTypeConstraint['fieldConstraints'];
    renderOverride?: Partial<RenderConfig>;
  } = {}
): SectionQuestionTypeConstraint {
  return {
    questionTypeId,
    ...options,
  };
}

// 重新声明类型以便本地使用（须与 ../types ValidationError 字段对齐）
interface ValidationError {
  field: string;
  message: string;
  code?: string;
  severity?: 'blocking' | 'warning';
}

interface FieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'url' | 'text';
  required: boolean;
  description?: string;
  validator?: (value: any) => boolean | string;
  defaultValue?: any;
}

interface RenderConfig {
  layout?: 'default' | 'audio-first' | 'passage-first' | 'image-first';
  showFields?: string[];
  component?: string;
  fieldConfig?: Record<
    string,
    {
      label?: string;
      placeholder?: string;
      component?: string;
    }
  >;
}

interface ExamSpecificQuestionTypeDefinition {
  id: string;
  baseType: QuestionType;
  name: string;
  requiredStimulus?: Array<'audio' | 'passage' | 'image'>;
  responseMode?: 'closed' | 'open_ended';
  fieldOverrides?: Partial<Record<string, FieldDefinition>>;
  extraFields?: Record<string, FieldDefinition>;
  renderConfigOverride?: Partial<RenderConfig>;
  customValidator?: (question: any) => ValidationError[];
  constraints?: Record<string, any>;
}

interface SectionQuestionTypeConstraint {
  questionTypeId: string;
  countLimit?: { min: number; max: number };
  fieldConstraints?: Record<
    string,
    {
      required?: boolean;
      allowedValues?: any[];
      min?: number;
      max?: number;
    }
  >;
  renderOverride?: Partial<RenderConfig>;
}
